import React from 'react';
import { SignOutButton } from '../buttons';
import { SimpleDialog } from '../SimpleDialog';
import { useNetworkState } from '@uidotdev/usehooks';
import { Community, HeadsetHelp, ScanQrCode, Wifi, WifiWarning, WifiXmark } from 'iconoir-react';
import { useNavigate } from '@tanstack/react-router';

/**
 * ECN Subtask 8 (CU-86ahjxqvp) — the agent-only minimal chrome variant.
 *
 * Chosen once at the chrome root (see _authenticated.tsx `WithHeader`) by
 * persona, NOT via per-item conditionals inside the patient Header — so an
 * agent-only user (isMIDEOFamilyMember && !isPatient) gets the minimal shell on
 * EVERY route, never a patient-chrome affordance leak (redesign §3).
 *
 * Minimal = /sessions (the agent's whole world) + help + sign-out. Deliberately
 * omits the patient Header's chart Home button, telehealth Join-Call, and the
 * AccountLink profile menu — all patient-only. Reads NO patient/chart data, so it
 * cannot crash for a chart-less agent.
 */
export const AgentHeader = () => {
  const network = useNetworkState();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [networkStatus, setNetworkStatus] = React.useState('online');

  const handleActionClick = () => {
    window.open('mailto:info@mideohealth.com');
    setOpen(false);
  };

  React.useEffect(() => {
    if (networkStatus === 'offline' && network.online === true) {
      setNetworkStatus('unstable');
      return;
    }
    if (networkStatus === 'online' && network.online === true) {
      setNetworkStatus('online');
      return;
    }
    if (network.online === false) {
      setNetworkStatus('offline');
      return;
    }
  }, [network, networkStatus]);

  return (
    <>
      {/* CU-86ajd3yt0: made responsive to match the patient Header fix —
          h-full flex row that contains its content, gap-based spacing (no fixed
          ml-16), and network-status TEXT hidden on small screens (icon conveys
          status). Less crowded than the patient header, but shares the same
          fragility, so kept consistent. */}
      <div className="bg-mideo-darkred h-12 w-full px-4 sm:px-8">
        <div
          id="header-container"
          className="flex h-full flex-row items-center justify-between gap-2"
        >
          <div className="flex flex-row items-center gap-2 sm:gap-4">
            {/* MIDEO Scan Activity (/sessions) — the agent shell's home. Same
                icon + label as the patient Header entry for one-route-one-name
                parity (CU-86aj1c9ev). */}
            <button
              onClick={() => navigate({ to: '/sessions' })}
              style={{ cursor: 'pointer' }}
              aria-label="MIDEO Scan Activity"
            >
              <ScanQrCode height={'2em'} width={'2em'} color="#fff" />
            </button>
            {/* ECN Subtask 9: the agent-only shell's one non-/sessions surface. */}
            <button
              onClick={() => navigate({ to: '/myMideoFamily' })}
              style={{ cursor: 'pointer' }}
              aria-label="My MIDEO Family"
            >
              <Community height={'2em'} width={'2em'} color="#fff" />
            </button>
          </div>
          {networkStatus === 'offline' && (
            <div className="flex flex-row items-center gap-1">
              <WifiXmark height={'2em'} width={'2em'} color="#f56565" />
              <div className="hidden font-bold text-red-500 sm:block">Offline</div>
            </div>
          )}
          {networkStatus === 'unstable' && (
            <div className="flex flex-row items-center gap-1">
              <WifiWarning height={'2em'} width={'2em'} color="#FFEB3B" />
              <div className="hidden font-bold text-yellow-500 sm:block">Unstable Connection</div>
            </div>
          )}
          {networkStatus === 'online' && (
            <div className="flex flex-row items-center gap-1">
              <Wifi height={'2em'} width={'2em'} color="#48bb78" />
              <p className="hidden font-bold text-green-500 sm:block">Online</p>
            </div>
          )}
          <div className="flex flex-row items-center gap-2 sm:gap-4">
            <button onClick={() => setOpen(true)} style={{ cursor: 'pointer' }} aria-label="Need help?">
              <HeadsetHelp height={'2em'} width={'2em'} color="#fff" />
            </button>
            <SignOutButton />
          </div>
        </div>
      </div>
      <SimpleDialog
        open={open}
        setOpen={setOpen}
        title="Need Help?"
        content="Contact us by phone at 844-MIDEO-4U or click the button below to send us an email."
        actionButtonTitle="Send Email"
        actionButtonHandler={handleActionClick}
      />
    </>
  );
};
